/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file LinkedinButton.js
 * @description
 * The "write on LinkedIn" action that sits next to every e-mail action. It renders NOTHING unless the
 * signed-in user connected their LinkedIn account AND the record carries a LinkedIn person URL, so it is
 * safe to drop beside any mail button without gating at the call site. Tapping it opens
 * {@link LinkedinComposeSheet} for the record (message gets logged on the lead / customer / contact).
 *
 * Two looks: `variant="icon"` (round icon button for action rows) and `variant="row"` (a labelled
 * outline button for detail screens).
 */

import React, {useState} from 'react';
import {StyleSheet, Text, TouchableOpacity} from 'react-native';
import {useTranslation} from 'react-i18next';

import Icon from '../management/SafeIcon';
import {useManagementTheme} from '../../helpers/managementTheme';
import useLinkedinStatus from '../../hooks/useLinkedinStatus';
import useLinkedinMatch from '../../hooks/useLinkedinMatch';
import {resolveLinkedinUrl} from '../../helpers/linkedinUtils';
import LinkedinComposeSheet, {LINKEDIN_BLUE} from './LinkedinComposeSheet';

/**
 * @param {Object} props
 * @param {object} [props.record] CRM record to read the LinkedIn URL from (ignored when `linkedinUrl` is given).
 * @param {string} [props.linkedinUrl] Explicit profile URL.
 * @param {string} [props.email] When there is no URL, the address is resolved to a CRM record that has one.
 * @param {string} [props.providerId] LinkedIn provider id (alternative to a URL).
 * @param {('lead'|'customer'|'contact')} [props.entityType] CRM kind for activity logging.
 * @param {string} [props.entityId] CRM id for activity logging.
 * @param {string} [props.followUpId] Open follow-up the send completes (channel linkedin).
 * @param {(result: object) => void} [props.onSent] Called after a successful send.
 * @param {{name?: string, company?: string, email?: string, phone?: string, position?: string}} [props.contact] Template context.
 * @param {('icon'|'row'|'chip')} [props.variant='icon'] `chip` = pill that matches the follow-up action bar buttons.
 * @param {number} [props.size=36] Diameter of the icon variant.
 * @param {object} [props.style]
 * @returns {JSX.Element|null}
 */
const LinkedinButton = ({record, linkedinUrl, email, providerId, entityType, entityId, followUpId, onSent, contact, variant = 'icon', size = 36, style}) => {
    const m = useManagementTheme();
    const {t} = useTranslation();
    const {ready} = useLinkedinStatus();
    const [open, setOpen] = useState(false);
    const directUrl = linkedinUrl || resolveLinkedinUrl(record);
    const {match} = useLinkedinMatch(email || record?.email, Boolean(directUrl || providerId));
    const url = directUrl || match?.linkedinUrl || '';

    if (!ready || (!url && !providerId)) {
        return null;
    }

    const recipient = {
        name: contact?.name || record?.name || record?.displayName || match?.name || '',
        company: contact?.company || record?.company || record?.companyName || match?.company || '',
        email: contact?.email || email || record?.email || '',
        phone: contact?.phone || record?.phone || '',
        position: contact?.position || record?.position || record?.title || '',
        linkedinUrl: url,
        providerId,
        entityType: entityType || match?.entityType,
        entityId: entityId || match?.entityId,
        followUpId,
    };

    return (
        <>
            {variant === 'chip' ? (
                <TouchableOpacity
                    onPress={() => setOpen(true)}
                    activeOpacity={0.7}
                    accessibilityRole="button"
                    style={[styles.chip, {borderColor: m.colors.border, backgroundColor: m.colors.surfaceAlt}, style]}
                >
                    <Icon name="linkedin" size={17} color={LINKEDIN_BLUE} />
                    <Text style={[styles.chipText, {color: m.colors.heading}]} numberOfLines={1}>{t('linkedin.short', 'LinkedIn')}</Text>
                </TouchableOpacity>
            ) : variant === 'row' ? (
                <TouchableOpacity
                    onPress={() => setOpen(true)}
                    activeOpacity={0.8}
                    style={[styles.rowBtn, {borderColor: LINKEDIN_BLUE}, style]}
                    accessibilityRole="button"
                    accessibilityLabel={t('linkedin.write_message', 'LinkedIn üzenet írása')}
                >
                    <Icon name="linkedin" size={18} color={LINKEDIN_BLUE} style={{marginRight: 8}} />
                    <Text style={[styles.rowText, {color: LINKEDIN_BLUE}]}>{t('linkedin.write_message', 'LinkedIn üzenet írása')}</Text>
                </TouchableOpacity>
            ) : (
                <TouchableOpacity
                    onPress={() => setOpen(true)}
                    hitSlop={8}
                    style={[styles.iconBtn, {width: size, height: size, borderRadius: size / 2, backgroundColor: m.colors.surfaceAlt, borderColor: m.colors.border}, style]}
                    accessibilityRole="button"
                    accessibilityLabel={t('linkedin.write_message', 'LinkedIn üzenet írása')}
                >
                    <Icon name="linkedin" size={Math.round(size * 0.55)} color={LINKEDIN_BLUE} />
                </TouchableOpacity>
            )}
            <LinkedinComposeSheet visible={open} recipient={recipient} onClose={() => setOpen(false)} onSent={onSent} />
        </>
    );
};

const styles = StyleSheet.create({
    iconBtn: {alignItems: 'center', justifyContent: 'center', borderWidth: 1},
    rowBtn: {flexDirection: 'row', alignItems: 'center', justifyContent: 'center', borderWidth: 1.5, borderRadius: 14, paddingHorizontal: 14, paddingVertical: 10},
    chip: {flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, borderRadius: 999, borderWidth: 1, paddingHorizontal: 14, paddingVertical: 10, flexGrow: 1},
    chipText: {fontSize: 13, fontWeight: '700'},
    rowText: {fontSize: 14, fontWeight: '800'},
});

export default LinkedinButton;
